import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '../../../../../shared/interfaces/constants';
import { MatOption } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import {
	ClientAuthorControllerService,
	ClientMediumControllerService,
	ManagementAuthorResponse,
	ManagementMediumClippingControllerService,
	ManagementMediumResponse,
	ManagementTagResponse,
} from 'api-clipping';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SharedService } from '../../../../../shared/services/shared.service';
import { CommonModule } from '@angular/common';
import { ManagementClientResponse } from 'api-client';

@Component({
	selector: 'app-clips-filters',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatCardModule,
		MatDividerModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatSelectModule,
		MatSliderModule,
		CdkDropList,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './clips-filters.component.html',
	styleUrl: './clips-filters.component.scss',
})
export class ClipsFiltersComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	@Input() role!: 'admin' | 'client' | 'landing';
	@Input() clientsList: any;
	@Input() resetFilters: any;
	@Input() apiLoading!: boolean;
	@Input() hasCompanyTags = false;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	filtersFormGroup: FormGroup = new FormGroup({});
	clipTypes = Constants.CLIP_TYPES;
	filteredMediums: ManagementMediumResponse[] | undefined;
	filteredAuthors: ManagementAuthorResponse[] | undefined;
  filteredClients: ManagementClientResponse[] | undefined;
	@ViewChild('selectAllMediumTypes') private selectAllMediumTypes!: MatOption;

	DAYS_RANGE = 3;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	readonly VISIBILITY_INDEX_RANGE = { min: 0, max: 100 };
	readonly SENTIMENT_SCORE_RANGE = { min: -100, max: 100 };

	filtersActive = false;

	constructor(
		private fb: FormBuilder,
		public tagsDropAssign: TagsDropAssignService,
		public utils: UtilsService,
		private apiClippingMedium: ManagementMediumClippingControllerService,
		private apiClientClippingMedium: ClientMediumControllerService,
		private apiClientClippingAuthor: ClientAuthorControllerService,
		private sharedService: SharedService
	) {
		this.formInitializer();
	}

	ngOnInit(): void {
		this.formControlWatchers();

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'ClipsFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.selectAllMediumTypes.select();
			this.mediumTypeSelection();
		}, 1000);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetFilters'] && !changes['resetFilters'].currentValue) {
			this.filtersActive = false;
			this.resetForm();
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			clientId: [],
			types: [[]],
			searchText: [],
			tagIds: [],
			dateFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			dateTo: [this.DATE_RANGE_LIMITS.dateTo],
			mediumId: [],
			authorId: [],
			visibilityIndexFrom: [this.VISIBILITY_INDEX_RANGE.min],
			visibilityIndexTo: [this.VISIBILITY_INDEX_RANGE.max],
			sentimentScoreFrom: [this.SENTIMENT_SCORE_RANGE.min],
			sentimentScoreTo: [this.SENTIMENT_SCORE_RANGE.max],
			main: [true],

			flags: [],
			tagsAll: [false],
			onlyMain: [true],
			noTags: [false],
			onlyTagged: [false],
		});
	}

	private formControlWatchers() {
		if (this.filtersFormGroup.controls['authorId']) {
			this.filtersFormGroup.controls['authorId'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchAuthors(searchTerm);
			});
		}

		if (this.filtersFormGroup.controls['mediumId']) {
			this.filtersFormGroup.controls['mediumId'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchMediums(searchTerm);
			});
		}

    if (this.filtersFormGroup.controls['clientId']) {
			this.filtersFormGroup.controls['clientId'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchClients(searchTerm);
			});
		}

		if (this.filtersFormGroup.controls['searchText']) {
			this.filtersFormGroup.controls['searchText'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.filtersFormGroup.controls['main'].setValue(searchTerm ? false : true);
				this.filtersFormGroup.controls['onlyMain'].setValue(searchTerm ? false : true);
				this.filtersFormGroup.controls['flags'].setValue([searchTerm ? this.filtersFormGroup.controls['flags'].value : 'onlyMain']);
			});
		}

		if (this.filtersFormGroup.controls['flags']) {
			this.filtersFormGroup.controls['flags'].valueChanges
				.pipe(
					debounceTime(450),
					distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
					takeUntil(this.onDestroy)
				)
				.subscribe(value => {
					if (value) {
						if (value.includes('noTags')) {
							this.filtersFormGroup.controls['flags'].setValue(['noTags']);
						} else if (value.includes('onlyTagged')) {
							this.filtersFormGroup.controls['flags'].setValue(['onlyTagged']);
						}
            else {
							this.filtersFormGroup.controls['noTags'].setValue(false);
              this.filtersFormGroup.controls['onlyTagged'].setValue(false);
						}

						value.forEach((flag: string) => {
							this.filtersFormGroup.controls[flag].setValue(true);
						});
					}
				});
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();
		}

		this.filtersFormGroup.controls['types'].setValue([]);
		if (this.selectAllMediumTypes) {
			this.selectAllMediumTypes.select();
			this.mediumTypeSelection();
		}

    this.filteredClients = this.clientsList;

		this.filtersFormGroup.controls['main'].setValue(true);
		this.filtersFormGroup.controls['noTags'].setValue(false);
		this.filtersFormGroup.controls['onlyTagged'].setValue(false);

		this.filtersFormGroup.controls['dateFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['dateTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);
		this.filtersFormGroup.controls['tagsAll'].setValue(false);
		this.filtersFormGroup.controls['onlyMain'].setValue(true);
		this.filtersFormGroup.controls['flags'].setValue(['onlyMain']);

		this.filtersFormGroup.controls['visibilityIndexFrom'].setValue(this.VISIBILITY_INDEX_RANGE.min);
		this.filtersFormGroup.controls['visibilityIndexTo'].setValue(this.VISIBILITY_INDEX_RANGE.max);
		this.filtersFormGroup.controls['sentimentScoreFrom'].setValue(this.SENTIMENT_SCORE_RANGE.min);
		this.filtersFormGroup.controls['sentimentScoreTo'].setValue(this.SENTIMENT_SCORE_RANGE.max);

		// if (formDirective) {
		// 	this.filtersActive = false;
		// 	this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
		// }
		// For client role, restore original preselected tags instead of leaving them unchanged
		// For admin role, deselect all tags
		if (this.role === 'admin') {
			this.sharedService.requestActionInComponent.next({
				componentName: 'TagsComponent',
				action: 'DESELECT_TAGS',
			});
		} else if (this.role === 'client') {
			this.sharedService.requestActionInComponent.next({
				componentName: 'TagsComponent',
				action: 'RESELECT_ORIGINAL_TAGS',
			});
		}

		this.filterListItems();
	}

	getFiltersFormData() {
		const formData = this.filtersFormGroup.value;

		let dateFrom = this.filtersFormGroup.controls['dateFrom'].value;
		if (dateFrom) {
			dateFrom = new Date((dateFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let dateTo = this.filtersFormGroup.controls['dateTo'].value;
		if (dateTo) {
			dateTo = new Date((dateTo as Date).setHours(0, 0, 0));
			dateTo = new Date(new Date(dateTo).setDate(new Date(dateTo).getDate() + 1)).getTime();
		}

		formData.dateFrom = dateFrom;
		formData.dateTo = dateTo;

		return this.filtersFormGroup.value;
	}

	getDateLimitRange() {
		// Requested by client to have full range active
		// const fromDate = this.filtersFormGroup.controls['dateFrom'].value;
		// const toDate = this.filtersFormGroup.controls['dateTo'].value ? this.filtersFormGroup.controls['dateTo'].value : this.DATE_RANGE_LIMITS.dateTo;
		// if (fromDate && new Date(fromDate).getTime() + this.utils.convertToMiliseconds('DAY', this.DAYS_RANGE) <= new Date(toDate).getTime()) {
		// 	return new Date(new Date(fromDate).setDate(fromDate.getDate() + this.DAYS_RANGE));
		// }
		// return toDate;
		return this.filtersFormGroup.controls['dateTo'].value;
	}

	/** Select medium type*/
	mediumTypeSelection(itemSelected?: Event) {
		if (itemSelected) {
			if (this.selectAllMediumTypes.selected) {
				this.selectAllMediumTypes.deselect();
			} else if (this.filtersFormGroup.controls['types'].value.length === this.clipTypes.length) {
				this.selectAllMediumTypes.select();
			}
		} else {
			this.filtersFormGroup.controls['types'].patchValue(this.selectAllMediumTypes.selected ? ['ALL', ...this.clipTypes.map(item => item.id)] : [], 0);
		}
	}

	/** Filter input medium name */
	private getMediumSearchObservable(searchTerm: string): Observable<any[]> {
		switch (this.role) {
			case 'admin':
				return this.apiClippingMedium.searchMediums(searchTerm);
			case 'client':
				return this.apiClientClippingMedium.searchMediumsClient(searchTerm);
			default:
				return this.apiClientClippingMedium.searchMediumsClient(searchTerm);
		}
	}

	private searchMediums(searchTerm: string) {
		if (searchTerm && searchTerm.length >= 1) {
			this.getMediumSearchObservable(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: (response: ManagementMediumResponse[]) => {
						this.filteredMediums = response;
					},
				});
		}
	}
	displayFn(medium: ManagementMediumResponse): string {
		return medium && medium.id ? this.utils.getParameterByLanguageScript(medium, 'name') : '';
	}

	/** Filter input author name */
	private searchAuthors(searchTerm: string) {
		if (searchTerm && searchTerm.length >= 1) {
			this.apiClientClippingAuthor
				.searchAuthorsClient(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: (response: ManagementAuthorResponse[]) => {
						this.filteredAuthors = response;
					},
				});
		}
	}
	displayAuthorFn(author: ManagementAuthorResponse): string {
		return author && author.id ? this.utils.getParameterByLanguageScript(author, 'name') : '';
	}

  /** Filter input client name */
  private searchClients(searchTerm: string) {
    if (searchTerm && searchTerm.length >= 1) {
      this.filteredClients = this.clientsList.filter((client: ManagementClientResponse) =>
        client.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    } else {
      this.filteredClients = this.clientsList;
    }
  }
  displayClientFn(client: ManagementClientResponse): string {
		return client && client.name ? client.name : '';
	}

	applySentimentPreset(preset: 'positive' | 'neutral' | 'negative') {
		const ranges: Record<typeof preset, { from: number; to: number }> = {
			positive: { from: 1, to: this.SENTIMENT_SCORE_RANGE.max },
			neutral: { from: 0, to: 0 },
			negative: { from: this.SENTIMENT_SCORE_RANGE.min, to: -1 },
		};
		const { from, to } = ranges[preset];
		this.filtersFormGroup.controls['sentimentScoreFrom'].setValue(from);
		this.filtersFormGroup.controls['sentimentScoreTo'].setValue(to);
	}

	isSentimentPresetActive(preset: 'positive' | 'neutral' | 'negative'): boolean {
		const from = this.filtersFormGroup.controls['sentimentScoreFrom'].value;
		const to = this.filtersFormGroup.controls['sentimentScoreTo'].value;
		switch (preset) {
			case 'positive':
				return from === 1 && to === this.SENTIMENT_SCORE_RANGE.max;
			case 'neutral':
				return from === 0 && to === 0;
			case 'negative':
				return from === this.SENTIMENT_SCORE_RANGE.min && to === -1;
		}
	}

	/** FILTERING: construct DTO with all filter data*/
	filterListItems() {
		this.filtersActive = false;

		let dateFrom = this.filtersFormGroup.controls['dateFrom'].value;
		if (dateFrom) {
			dateFrom = new Date((dateFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let dateTo = this.filtersFormGroup.controls['dateTo'].value;
		if (dateTo) {
			dateTo = new Date((dateTo as Date).setHours(0, 0, 0));
			dateTo = new Date(new Date(dateTo).setDate(new Date(dateTo).getDate() + 1)).getTime();
		}

		if (this.tagsDropAssign.manuallyselectedTags) {
			this.filtersFormGroup.controls['tagIds'].setValue(this.tagsDropAssign.manuallyselectedTags.map(tag => tag.id));
		} else {
			this.filtersFormGroup.controls['tagIds'].setValue([]);
		}

		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.dateFrom = dateFrom;
		filterDto.dateTo = dateTo;

		if (this.role === 'client') {
			filterDto.publicationDateFrom = dateFrom;
			filterDto.publicationDateTo = dateTo;
		} else if (this.role === 'admin') {
			filterDto.createdAtFrom = dateFrom;
			filterDto.createdAtTo = dateTo;
		}

		if (this.filtersFormGroup.controls['types'].value) {
			filterDto.types = this.filtersFormGroup.controls['types'].value.find((item: string) => item === 'ALL') ? [] : this.filtersFormGroup.controls['types'].value;
		}

		filterDto.mediumId = this.filtersFormGroup.controls['mediumId'].value ? this.filtersFormGroup.controls['mediumId'].value.id : null;
		filterDto.authorId = this.filtersFormGroup.controls['authorId'].value ? this.filtersFormGroup.controls['authorId'].value.id : null;
    filterDto.clientId = this.filtersFormGroup.controls['clientId'].value ? this.filtersFormGroup.controls['clientId'].value.id : null;

		if (this.role === 'client') {
			filterDto.visibilityIndexFrom = filterDto.visibilityIndexFrom !== this.VISIBILITY_INDEX_RANGE.min ? filterDto.visibilityIndexFrom : null;
			filterDto.visibilityIndexTo = filterDto.visibilityIndexTo !== this.VISIBILITY_INDEX_RANGE.max ? filterDto.visibilityIndexTo : null;
			filterDto.sentimentScoreFrom = filterDto.sentimentScoreFrom !== this.SENTIMENT_SCORE_RANGE.min ? filterDto.sentimentScoreFrom : null;
			filterDto.sentimentScoreTo = filterDto.sentimentScoreTo !== this.SENTIMENT_SCORE_RANGE.max ? filterDto.sentimentScoreTo : null;
		} else {
			filterDto.visibilityIndexFrom = null;
			filterDto.visibilityIndexTo = null;
			filterDto.sentimentScoreFrom = null;
			filterDto.sentimentScoreTo = null;
		}

		if (this.role === 'client') {
			filterDto.main = filterDto.mediumId || filterDto.authorId || filterDto.types.length !== 0 ? false : true;
		} else if (this.role === 'admin') {
			filterDto.main = filterDto.mediumId || filterDto.types.length !== 0 ? false : true;
		}

		this.filtersActive = !Object.keys(filterDto).every((key: string) => filterDto[key] === null || (filterDto[key] && filterDto[key].length === 0));

		if (this.role === 'admin') {
			filterDto.autoTagged = this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('autoTagged') ? true : false;
			filterDto.autoGrouped = this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('autoGrouped') ? true : false;
		} else {
			filterDto.autoTagged = false;
			filterDto.autoGrouped = false;
		}

		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
		// if (this.filtersActive) {
		// 	if (this.filtersFormGroup.controls['mediumId'].value) {
		// 		this.actionRequested.emit({ action: 'FILTER_BY_MEDIUM', filters: filterDto, id: 0 });
		// 	} else {
		// 		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
		// 	}
		// } else {
		// 	this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
		// }
	}
}
